/**
 * Offline / failed-push queue for adaptive experience events (localStorage).
 * Flushes when back online or on app resume (Capacitor).
 */

import { safeParseJson } from './storage-utils';
import type { ActivityEvent } from './adaptive-experience';
import { pushAdaptiveExperienceEvents } from './adaptive-experience-api';

export const ADAPTIVE_SYNC_QUEUE_KEY = 'tension_adaptive_sync_queue_v1';
const QUEUE_KEY = ADAPTIVE_SYNC_QUEUE_KEY;
const MAX_QUEUED = 120;

function readQueue(): ActivityEvent[] {
  const raw = localStorage.getItem(QUEUE_KEY);
  const parsed = safeParseJson<unknown>(raw, []);
  return Array.isArray(parsed)
    ? (parsed as ActivityEvent[]).filter((e) => e && typeof e.at === 'number' && e.type && e.clientEventId)
    : [];
}

function writeQueue(events: ActivityEvent[]): void {
  try {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(events.slice(-MAX_QUEUED)));
  } catch {
    /* quota */
  }
}

function mergeKey(e: ActivityEvent): string {
  return e.clientEventId ? `id:${e.clientEventId}` : `${e.type}:${e.at}`;
}

export function enqueueAdaptiveSyncFailed(events: ActivityEvent[]): void {
  if (events.length === 0) return;
  const map = new Map<string, ActivityEvent>();
  for (const e of readQueue()) map.set(mergeKey(e), e);
  for (const e of events) map.set(mergeKey(e), e);
  writeQueue([...map.values()].sort((a, b) => a.at - b.at));
}

export function dequeueAdaptiveSynced(clientEventIds: string[]): void {
  if (clientEventIds.length === 0) return;
  const drop = new Set(clientEventIds);
  writeQueue(readQueue().filter((e) => !e.clientEventId || !drop.has(e.clientEventId)));
}

/** Push queued events in batches; returns number successfully attempted (best-effort). */
export async function flushAdaptiveSyncQueue(accessToken: string | null | undefined): Promise<number> {
  if (!accessToken) return 0;
  const q = readQueue();
  if (q.length === 0) return 0;
  const BATCH = 50;
  let pushed = 0;
  for (let i = 0; i < q.length; i += BATCH) {
    const slice = q.slice(i, i + BATCH);
    try {
      await pushAdaptiveExperienceEvents(accessToken, slice);
      dequeueAdaptiveSynced(slice.map((e) => e.clientEventId).filter((id): id is string => Boolean(id)));
      pushed += slice.length;
    } catch {
      break;
    }
  }
  return pushed;
}

export function clearAdaptiveSyncQueue(): void {
  try {
    localStorage.removeItem(QUEUE_KEY);
  } catch {
    /* ignore */
  }
}
